import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
//hooks
import { useOrganization } from "../hooks/useOrganization";
import { useForm } from "react-hook-form";
import { useToast } from "../contexts/ToastContext";
//Api
import API from "../API";
//config
import { ORGANIZATION_FEATURE_GROUPS } from "../config/organizationFeatures";
import { ORGANIZATION_LIMITS } from "../config/organizationLimits";

//components
import Toggle from "./Toggle";
import Input from "./Input";
import Dropdown from "./Dropdown";
import Spinner from "./Spinner";
import LabelRoll from "./LabelRoll";

const OrganizationInfo = ({ organization, param }) => {
  const { register, handleSubmit, reset } = useForm();
  const { showToast } = useToast();
  const { register: registerMyOrdersSettings, handleSubmit: handleSubmitMyOrdersSettings, reset: resetMyOrdersSettings } = useForm();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [showUsers, setShowUsers] = useState(false);
  const [myOrdersSettings, setMyOrdersSettings] = useState(null);
  const [loadingMyOrdersSettings, setLoadingMyOrdersSettings] = useState(false);
  const [savingMyOrdersSettings, setSavingMyOrdersSettings] = useState(false);

  // Tenant data cleanup (see output/US-admin-tenant-data-cleanup.md)
  const [cleanupArticles, setCleanupArticles] = useState(false);
  const [cleanupPrintHistory, setCleanupPrintHistory] = useState(false);
  const [cleanupConfirmName, setCleanupConfirmName] = useState('');
  const [cleanupRows, setCleanupRows] = useState(null);
  const [cleanupMode, setCleanupMode] = useState('');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupError, setCleanupError] = useState('');
  const [cleanupPollWarning, setCleanupPollWarning] = useState('');
  // "Etikettrulle": one layer added per status fetch, wraps back to an empty
  // bobbin after 25 — visual proof that polling is still alive, in place of a
  // static spinner/seconds-ago readout.
  const [cleanupPollLayers, setCleanupPollLayers] = useState(0);
  const MAX_CLEANUP_POLL_LAYERS = 25;

  const cleanupNameMatches =
    cleanupConfirmName.trim() === (organization?.name || '') && cleanupConfirmName.length > 0;
  const cleanupHasOperation = cleanupArticles || cleanupPrintHistory;

  // Cleanup runs in the background on the API (large tenants take longer than Azure
  // App Service's fixed 230s request timeout — see output/CHANGELOG-tenant-cleanup-batching.md).
  // Run starts, then this polls the status endpoint until Completed/Failed.
  const CLEANUP_POLL_INTERVAL_MS = 3000;

  // Guards against calling setState after unmount: a poll can be mid-flight (or
  // waiting between polls) for minutes on a large tenant, and the admin may
  // navigate away from this page before the run finishes.
  const isMountedRef = useRef(true);
  const cleanupPollTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (cleanupPollTimeoutRef.current) {
        clearTimeout(cleanupPollTimeoutRef.current);
        cleanupPollTimeoutRef.current = null;
      }
    };
  }, []);

  const waitBeforeNextCleanupPoll = () =>
    new Promise((resolve) => {
      cleanupPollTimeoutRef.current = setTimeout(resolve, CLEANUP_POLL_INTERVAL_MS);
    });

  // One layer per status fetch; a full roll (25) empties back to the bare bobbin
  // on the next tick rather than growing forever.
  const addCleanupPollLayer = () =>
    setCleanupPollLayers((layers) => (layers >= MAX_CLEANUP_POLL_LAYERS ? 0 : layers + 1));

  const pollCleanupStatus = async (organizationId, runId) => {
    let response;
    try {
      response = await API.getTenantDataCleanupStatus(organizationId, runId);
    } catch (error) {
      if (!isMountedRef.current) return;

      // A 404 means this runId/organizationId is invalid server-side — the run was
      // just created, so this can't be a transient blip. Retrying forever against a
      // resource that will never resolve isn't useful, so treat it as terminal.
      if (error.response?.status === 404) {
        setCleanupPollWarning('');
        setCleanupError(
          error.response?.data?.message || error.response?.data?.error || 'Cleanup run not found'
        );
        setCleanupLoading(false);
        return;
      }

      // Any other failure (network error, timeout, 5xx) is treated as transient:
      // warn, but keep polling on the same cadence.
      setCleanupPollWarning('Could not reach the server, retrying…');
      addCleanupPollLayer();
      await waitBeforeNextCleanupPoll();
      if (!isMountedRef.current) return;
      await pollCleanupStatus(organizationId, runId);
      return;
    }

    if (!isMountedRef.current) return;

    setCleanupPollWarning('');
    addCleanupPollLayer();

    const { status, mode, rows, errorMessage } = response.data;

    if (status === 'Completed') {
      setCleanupRows(rows || []);
      setCleanupMode(mode || '');
      setCleanupLoading(false);
      return;
    }
    if (status === 'Failed') {
      setCleanupError(errorMessage || 'Cleanup failed');
      setCleanupLoading(false);
      return;
    }

    await waitBeforeNextCleanupPoll();
    if (!isMountedRef.current) return;
    await pollCleanupStatus(organizationId, runId);
  };

  const runCleanup = async (dryRun) => {
    setCleanupLoading(true);
    setCleanupError('');
    setCleanupRows(null);
    setCleanupPollWarning('');
    setCleanupPollLayers(0);
    try {
      const response = await API.startTenantDataCleanup(organization.id, {
        includeArticles: cleanupArticles,
        includePrintHistory: cleanupPrintHistory,
        dryRun,
        confirmName: cleanupConfirmName.trim(),
      });
      await pollCleanupStatus(organization.id, response.data.runId);
    } catch (error) {
      if (!isMountedRef.current) return;
      setCleanupLoading(false);
      setCleanupRows(null);
      setCleanupError(
        error.response?.data?.message || error.response?.data?.error || error.message || 'Cleanup failed'
      );
    }
  };

  const fetchUsers = async (organizationId) => {
    setLoadingUsers(true);
    setUsersError('');
    try {
      const response = await API.getOrganizationUsers(organizationId);
      setUsers(response.data || []);
    } catch (error) {
      setUsersError('Failed to fetch users for the organization.');
    } finally {
      setLoadingUsers(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const payload = {
      adminEmail: data.adminEmail,
      id: data.id,
      globalCountersEnabled: data.globalCountersEnabled,
      importEnabled: data.importEnabled,
      widgetsEnabled: data.widgetsEnabled,
      maxLabels: data.maxLabels,
      maxPrinterModels: data.maxPrinterModels,
      maxUsers: data.maxUsers,
      maxWidgets: data.maxWidgets,
      maxPCLPrinters: data.maxPCLPrinters,
      maxAgents: data.maxAgents,
      myArticlesEnabled: data.myArticlesEnabled,
      myLabelsEnabled: data.myLabelsEnabled,
      myOrdersEnabled: data.myOrdersEnabled,
      name: data.name,
      pdfEnabled: data.pdfEnabled,
      pdfOnly: data.pdfOnly,
      printArticleEnabled: data.printArticleEnabled,
      printLabelEnabled: data.printLabelEnabled,
      quickPrintEnabled: data.quickPrintEnabled,
      printOrderEnabled: data.printOrderEnabled,
      connectorsEnabled: data.connectorsEnabled,
      agentsEnabled: data.agentsEnabled,
      printHistoryEnabled: data.printHistoryEnabled,
      integrationClientsEnabled: data.integrationClientsEnabled,
      integrationWebHooksEnabled: data.integrationWebHooksEnabled,
      labelVersionControlEnabled: data.labelVersionControlEnabled,
      sendToGroupEnabled: data.sendToGroupEnabled,
      subscriptionType: data.subscriptionType,
      standardLabelsEnabled: data.standardLabelsEnabled,
      setupStandardLabelsEnabled: data.setupStandardLabelsEnabled,
      organizationSettingsEnabled: data.organizationSettingsEnabled,
      subscriptionStatus: data.subscriptionStatus,
    };

    if (param === "new") {
      //console.log("POST")
      try {
        const response = await API.postOrganization(payload);
        if (response) {
          //console.log({response})
          showToast("Organization created");
        }
      } catch (error) {
        console.log(error);
        setWarning("Something went wrong: ", error);
      }
    } else {
      console.log("PUT");
      try {
        const response = await API.putOrganization(payload);
        if (response) {
          // console.log({response})
          showToast("Organization updated");
        }
      } catch (error) {
        console.log(error);
        setWarning("Something went wrong: ", error);
      }
    }
    setLoading(false);
  };

  const {
    setWarning,
    setLoading,
    adminEmail,
    invalidComboError,
    myLabelDisabledError,
    warning,
  } = useOrganization("");

  const subscriptionTypes = ["Free", "Starter", "Standard", "Business"];

  useEffect(() => {
    reset(organization);
  }, [organization, reset]);

  useEffect(() => {
    const fetchMyOrdersSettings = async () => {
      if (param !== "new" && organization?.id) {
        setLoadingMyOrdersSettings(true);
        try {
          const response = await API.getMyOrdersSettings(organization.id);
          setMyOrdersSettings(response.data);
          resetMyOrdersSettings(response.data);
        } catch (error) {
          // If settings don't exist, that's okay - they'll be created when saved
          if (error.response?.status !== 404) {
            console.error("Failed to fetch MyOrdersSettings:", error);
          }
        } finally {
          setLoadingMyOrdersSettings(false);
        }
      }
    };
    fetchMyOrdersSettings();
  }, [organization?.id, param, resetMyOrdersSettings]);

  const onSubmitMyOrdersSettings = async (data) => {
    if (param === "new" || !organization?.id) {
      showToast("Save the organization first before you can change My Orders settings", { type: "info" });
      return;
    }

    setSavingMyOrdersSettings(true);
    try {
      const response = await API.updateMyOrdersSettings(organization.id, data);
      setMyOrdersSettings(response.data);
      showToast("My Orders settings updated");
    } catch (error) {
      console.error("Failed to update MyOrdersSettings:", error);
      showToast("Could not update My Orders settings: " + (error.response?.data?.message || error.message), { type: "error" });
    } finally {
      setSavingMyOrdersSettings(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Status Banner */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {param === "new" ? (
              <>
                <i className="fas fa-plus-circle text-green-600 dark:text-green-400 text-lg"></i>
                <span className="text-lg font-semibold text-green-600 dark:text-green-400">Adding New Organization</span>
              </>
            ) : (
              <>
                <i className="fas fa-edit text-orange-600 dark:text-orange-400 text-lg"></i>
                <span className="text-lg font-semibold text-orange-600 dark:text-orange-400">Edit Mode</span>
              </>
            )}
          </div>
          {organization && (
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                organization.subscriptionStatus === "Active" 
                  ? "bg-green-500" 
                  : organization.subscriptionStatus === "Deleted"
                  ? "bg-red-500"
                  : "bg-gray-500"
              }`}></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {organization.subscriptionStatus}
              </span>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Organization Information */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
            <i className="fas fa-building mr-3 text-gray-600 dark:text-gray-400"></i>
            Organization Information
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Basic Info */}
            <div className="space-y-4">
              <Input
                text="Organization name"
                value={organization?.name}
                name="name"
                register={register}
              />
              <Input
                text="Street address"
                value="Street address 33"
                name="address"
                register={register}
              />
              <Input
                text="Postal address"
                value="PO 12345"
                name="postalAddress"
                register={register}
              />
              <Input
                text="Country"
                value="Sweden"
                name="country"
                register={register}
              />
              <Input
                text="Email"
                type="email"
                value="info@grafokett.se"
                name="email"
                register={register}
              />
              <Input
                text="Tenant admin email"
                type="email"
                value={adminEmail}
                name="adminEmail"
                register={register}
              />
            </div>
            
            {/* Right Column - Statistics */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <i className="fas fa-chart-bar mr-2 text-gray-600 dark:text-gray-400"></i>
                Statistics
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="fas fa-users text-gray-600 dark:text-gray-400 mr-2"></i>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Active Users</span>
                    </div>
                    <span className="text-lg font-bold text-gray-900 dark:text-white">N/A</span>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="fas fa-tags text-gray-600 dark:text-gray-400 mr-2"></i>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Printed Labels</span>
                    </div>
                    <span className="text-lg font-bold text-gray-900 dark:text-white">N/A</span>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="fas fa-print text-gray-600 dark:text-gray-400 mr-2"></i>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Print Jobs</span>
                    </div>
                    <span className="text-lg font-bold text-gray-900 dark:text-white">N/A</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
            <i className="fas fa-toggle-on mr-3 text-gray-600 dark:text-gray-400"></i>
            Features
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* Subscription Type */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <i className="fas fa-gem mr-2 text-gray-600 dark:text-gray-400"></i>
                Subscription Type
              </h3>
              <Dropdown
                values={subscriptionTypes}
                text="Select new type here"
                name="subscriptionType"
                register={register}
              />
              <p className="text-xs text-orange-600 dark:text-orange-400 mt-2 font-medium">
                Current plan: {organization?.subscriptionType}
              </p>
            </div>

            {ORGANIZATION_FEATURE_GROUPS.map((group) => (
              <div
                key={group.title}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600"
              >
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                  <i className={`${group.icon} mr-2 text-gray-600 dark:text-gray-400`}></i>
                  {group.title}
                </h3>
                <div className="space-y-3">
                  {group.items.map((item) => (
                    <Toggle
                      key={item.name}
                      name={item.name}
                      text={item.label}
                      register={register}
                    />
                  ))}
                </div>
                {group.title === "Labels & Printing" && myLabelDisabledError && (
                  <div className="mt-2 text-xs text-orange-600 dark:text-orange-400 font-medium">
                    Requires My Labels
                  </div>
                )}
                {group.title === "Labels & Printing" && invalidComboError && (
                  <div className="mt-2 text-xs text-orange-600 dark:text-orange-400 font-medium">
                    Toggle order or article
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* My Orders Settings */}
        {param !== "new" && organization?.id && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
              <i className="fas fa-cog mr-3 text-gray-600 dark:text-gray-400"></i>
              My Orders Settings
            </h2>
            
            {loadingMyOrdersSettings ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : (
              <form onSubmit={handleSubmitMyOrdersSettings(onSubmitMyOrdersSettings)} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <i className="fas fa-filter mr-2 text-gray-600 dark:text-gray-400"></i>
                        Filter & Display Options
                      </h3>
                      <div className="space-y-3">
                        <Toggle
                          name="hideZeroQuantityOrders"
                          text="Hide Zero Quantity Orders"
                          register={registerMyOrdersSettings}
                        />
                        <Toggle
                          name="showInactiveOrders"
                          text="Show Inactive Orders"
                          register={registerMyOrdersSettings}
                        />
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <i className="fas fa-calendar-alt mr-2 text-gray-600 dark:text-gray-400"></i>
                        Age Settings
                      </h3>
                      <div className="space-y-3">
                        <Input
                          type="number"
                          value={myOrdersSettings?.ordersMaxAgeInDays || ''}
                          name="ordersMaxAgeInDays"
                          text="Max Age in Days"
                          register={registerMyOrdersSettings}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <i className="fas fa-cog mr-2 text-gray-600 dark:text-gray-400"></i>
                        Order Options
                      </h3>
                      <div className="space-y-3">
                        <Toggle
                          name="enableOverrideOnOrder"
                          text="Enable Override on Order"
                          register={registerMyOrdersSettings}
                        />
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <i className="fas fa-file-alt mr-2 text-gray-600 dark:text-gray-400"></i>
                        Preview & Export
                      </h3>
                      <div className="space-y-3">
                        <Toggle
                          name="enablePdf"
                          text="Enable PDF"
                          register={registerMyOrdersSettings}
                        />
                        <Toggle
                          name="enableLabelPreview"
                          text="Enable Label Preview"
                          register={registerMyOrdersSettings}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={savingMyOrdersSettings}
                    className="px-6 py-2 bg-grafokett-blue text-white rounded-lg hover:bg-grafokett-blue-dark transition-colors duration-200 font-medium shadow-sm hover:shadow-md flex items-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingMyOrdersSettings ? (
                      <>
                        <Spinner />
                        <span className="ml-2">Saving...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2 text-sm"></i>
                        Save My Orders Settings
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Limits & Submit */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
            <i className="fas fa-chart-line mr-3 text-gray-600 dark:text-gray-400"></i>
            Limits & Configuration
          </h2>
          
          {warning.length > 0 && (
            <div className="bg-orange-50 dark:bg-orange-900 border border-orange-200 dark:border-orange-700 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <i className="fas fa-exclamation-triangle text-orange-600 dark:text-orange-400 mr-2"></i>
                <span className="text-sm text-orange-800 dark:text-orange-200 font-medium">
                  {warning}
                </span>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
            {ORGANIZATION_LIMITS.map((limit) => (
              <div
                key={limit.name}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
              >
                <div className="flex items-center justify-center mb-4">
                  <i className={`${limit.icon} text-gray-600 dark:text-gray-400 mr-2 text-lg`}></i>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {limit.label}
                  </span>
                </div>
                <div className="flex justify-center">
                  <div className="w-1/2">
                    <Input
                      type="number"
                      value={organization?.[limit.name]}
                      name={limit.name}
                      text=""
                      register={register}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex justify-end space-x-3">
            {param !== "new" && organization?.id && (
              <button
                type="button"
                onClick={async () => {
                  if (!showUsers) {
                    await fetchUsers(organization.id);
                  }
                  setShowUsers(!showUsers);
                }}
                className="px-6 py-2 bg-grafokett-blue text-white rounded-lg hover:bg-grafokett-blue-dark transition-colors duration-200 font-medium shadow-sm hover:shadow-md flex items-center text-sm"
              >
                <i className={`fas ${showUsers ? "fa-eye-slash" : "fa-users"} mr-2 text-sm`}></i>
                {showUsers ? "Hide Users" : "Show Users"}
              </button>
            )}
            <button
              type="submit"
              className="px-6 py-2 bg-gray-600 dark:bg-gray-500 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-400 transition-colors duration-200 font-medium shadow-sm hover:shadow-md flex items-center text-sm"
            >
              <i className={`fas ${param === "new" ? "fa-plus" : "fa-save"} mr-2 text-sm`}></i>
              {param === "new" ? "Create Organization" : "Save Changes"}
            </button>
          </div>
        </div>
      </form>
      
      {/* Tenant data cleanup */}
      {param !== "new" && organization?.id && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-red-300 dark:border-red-800 shadow-lg">
          <h2 className="text-xl font-bold text-red-600 dark:text-red-400 mb-2 flex items-center">
            <i className="fas fa-triangle-exclamation mr-3"></i>
            Tenant data cleanup
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Permanently clears selected data for <strong>{organization.name}</strong> only.
            Does not delete the tenant or its users. Counters, orders and labels
            (other than test-article links) are left untouched.
          </p>

          <div className="space-y-3 mb-4">
            <label className="flex items-center text-sm text-gray-800 dark:text-gray-200">
              <input
                type="checkbox"
                className="mr-2"
                checked={cleanupArticles}
                onChange={(e) => setCleanupArticles(e.target.checked)}
              />
              Articles
            </label>
            <label className="flex items-center text-sm text-gray-800 dark:text-gray-200">
              <input
                type="checkbox"
                className="mr-2"
                checked={cleanupPrintHistory}
                onChange={(e) => setCleanupPrintHistory(e.target.checked)}
              />
              Print history
            </label>
          </div>

          <div className="mb-4">
            <label
              htmlFor="cleanupConfirmName"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Type "{organization.name}" to enable Run cleanup
            </label>
            <input
              id="cleanupConfirmName"
              type="text"
              value={cleanupConfirmName}
              onChange={(e) => setCleanupConfirmName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>

          {cleanupError && (
            <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-3 mb-4 text-sm text-red-800 dark:text-red-200">
              {cleanupError}
            </div>
          )}

          {cleanupPollWarning && (
            <div className="bg-orange-100 dark:bg-orange-900 border border-orange-300 dark:border-orange-700 rounded-lg p-3 mb-4 text-sm text-orange-800 dark:text-orange-200">
              {cleanupPollWarning}
            </div>
          )}

          {cleanupLoading && (
            <div className="flex flex-col items-center py-4 space-y-3">
              <Spinner />
              <LabelRoll layers={cleanupPollLayers} />
            </div>
          )}

          {cleanupRows && (
            <div className="overflow-x-auto mb-4">
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                Result — mode: {cleanupMode}
              </p>
              <table className="min-w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Table</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Rows affected</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {cleanupRows.map((row, index) => (
                    <tr key={`${row.tableName}-${index}`}>
                      <td className="px-4 py-2 text-gray-900 dark:text-white">{row.tableName}</td>
                      <td className="px-4 py-2 text-gray-900 dark:text-white">{row.rowsAffected}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-red-600 dark:text-red-400 font-medium mb-3">
            Run cleanup permanently deletes the selected data. This cannot be undone.
          </p>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => runCleanup(true)}
              disabled={cleanupLoading || !cleanupHasOperation}
              className="px-4 py-2 text-sm font-medium bg-grafokett-blue text-white rounded-lg hover:bg-grafokett-blue-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Preview
            </button>
            <button
              type="button"
              onClick={() => runCleanup(false)}
              disabled={cleanupLoading || !cleanupHasOperation || !cleanupNameMatches}
              className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Run cleanup
            </button>
          </div>
        </div>
      )}

      {/* Users Section */}
      {showUsers && param !== "new" && organization?.id && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
            <i className="fas fa-users mr-3 text-gray-600 dark:text-gray-400"></i>
            Users in organization
          </h2>
          
          {loadingUsers && (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          )}
          
          {usersError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <p className="text-red-800 dark:text-red-200">{usersError}</p>
            </div>
          )}
          
          {!loadingUsers && !usersError && users.length === 0 && (
            <div className="text-center py-8 text-gray-600 dark:text-gray-400">
              <i className="fas fa-users text-4xl mb-4 opacity-50"></i>
              <p>No users found for this organization</p>
            </div>
          )}
          
          {!loadingUsers && !usersError && users.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Namn
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      E-post
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Roller
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.firstName} {user.lastName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {user.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.disabled 
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' 
                            : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        }`}>
                          {user.disabled ? 'Inaktiverad' : 'Aktiv'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {user.roles && user.roles.length > 0 
                          ? user.roles.map(r => r.name).join(', ')
                          : 'Inga roller'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Footer */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            If you are reading this you scrolled all the way to the bottom <span role="img" aria-label="laughing face">🤣</span>
          </p>
                <Link to="/joke">
                  <button className="px-6 py-2 bg-grafokett-orange text-white rounded-lg hover:bg-orange-600 transition-colors duration-200 font-medium shadow-sm hover:shadow-md flex items-center mx-auto text-sm">
                    <i className="fas fa-smile mr-2 text-sm"></i>
                    Enjoy a random joke at the 404 page
                  </button>
                </Link>
        </div>
      </div>
    </div>
  );
};

export default OrganizationInfo;
